import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Paper
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEnvelope } from '@fortawesome/free-solid-svg-icons'
import PropTypes from 'prop-types'

const TemplatePreviewDialog = ({
  open,
  onClose,
  template
}) => {
  if (!template) return null

  const previewData = {
    campaign_name: 'Sample Campaign',
    usage_percent: '85',
    plan_minutes: '500',
    current_month_minutes: '425',
    triggered_handle_time: '425 minutes',
    monthly_plan_size: '500',
  }

  const renderPreviewContent = (content) => {
    let rendered = content || ''
    
    const replacements = {
      '{campaign_name}': previewData.campaign_name,
      '{usage_percent}': previewData.usage_percent,
      '{plan_minutes}': previewData.plan_minutes,
      '{current_month_minutes}': previewData.current_month_minutes,
      '{triggered_handle_time}': previewData.triggered_handle_time,
      '{monthly_plan_size}': previewData.monthly_plan_size,
    }

    Object.entries(replacements).forEach(([placeholder, value]) => {
      rendered = rendered.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value)
    })

    return rendered
  }

  const previewSubject = renderPreviewContent(template.subject)
  const previewBody = renderPreviewContent(template.body)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <FontAwesomeIcon icon={faEye} style={{ marginRight: 8, color: '#2196f3' }} />
          Template Preview - {template.name}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mb={3}>
          <Typography variant="body2" color="textSecondary" paragraph>
            This preview shows how the email will look with sample campaign data. 
            Variable placeholders are replaced with example values.
          </Typography>
        </Box>

        {/* Email Preview */}
        <Paper variant="outlined" style={{ padding: 16, backgroundColor: '#fafafa' }}>
          {/* Email Header */}
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: 'var(--font-size-sm)' }}>
              <strong>From:</strong> {template.from_email || 'support@vetavirtual.com'}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: 'var(--font-size-sm)' }}>
              <strong>To:</strong> recipient@example.com
            </Typography>
          </Box>

          <Divider style={{ margin: '12px 0' }} />

          {/* Subject Line */}
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 4 }}>
              <strong>Subject:</strong>
            </Typography>
            <Typography variant="h6" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 500 }}>
              <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 8, color: 'var(--color-text-secondary)' }} />
              {previewSubject}
            </Typography>
          </Box>

          <Divider style={{ margin: '12px 0' }} />

          {/* Email Body */}
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 8 }}>
              <strong>Message Body:</strong>
            </Typography>
            <Paper 
              variant="outlined" 
              style={{ 
                padding: 16, 
                backgroundColor: 'var(--color-bg-white)',
                border: '1px solid var(--color-border)',
                minHeight: 120
              }}
            >
              <Typography 
                variant="body1" 
                style={{ 
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6,
                  fontFamily: '"Helvetica Neue", Arial, sans-serif'
                }}
              >
                {previewBody}
              </Typography>

              {template.include_dashboard_button && (
                <Box mt={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled
                    style={{ 
                      textTransform: 'none',
                      fontSize: 'var(--font-size-base)'
                    }}
                  >
                    View Dashboard
                  </Button>
                  <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                    Dashboard button will link to: https://dashboard.vetavirtual.com
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>

          {/* Template Info */}
          <Box mt={3} p={2} style={{ backgroundColor: '#e3f2fd', borderRadius: 4 }}>
            <Typography variant="body2" style={{ color: '#1976d2', fontSize: 'var(--font-size-sm)' }}>
              <strong>📝 Template Info:</strong>
            </Typography>
            <Typography variant="body2" style={{ color: '#1976d2', fontSize: 'var(--font-size-sm)' }}>
              • Variables are replaced with sample data for preview
            </Typography>
            <Typography variant="body2" style={{ color: '#1976d2', fontSize: 'var(--font-size-sm)' }}>
              • Actual emails will use real campaign usage data
            </Typography>
            {template.sendgrid_template_id && (
              <Typography variant="body2" style={{ color: '#1976d2', fontSize: 'var(--font-size-sm)' }}>
                • SendGrid Template ID: {template.sendgrid_template_id}
              </Typography>
            )}
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained" style={{ textTransform: 'none', fontWeight: 600 }}>
          Close Preview
        </Button>
      </DialogActions>
    </Dialog>
  )
}

TemplatePreviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  template: PropTypes.object
}

export default TemplatePreviewDialog